import { useState, useRef, useCallback } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import LanguageToggle from "./LanguageToggle";
import {
  MASTER_TERMS,
  ACCEPTABLE_USE_POLICY,
  PRIVACY_POLICY,
  DATA_PROCESSING_TERMS,
} from "../legal/legalDocuments";

const border = "rgba(0,245,255,0.15)";

// Scroll position within this many px of the bottom counts as "reached
// the end" — accounts for sub-pixel rounding across browsers.
const SCROLL_BOTTOM_THRESHOLD = 16;

// The main gate's 4 documents — matches 00_INSTALLATION_GUIDE.md's suggested
// clickwrap wording exactly ("Master Terms of Service, applicable Data
// Processing Terms, Acceptable Use Policy, and AI & Automated
// Decision-Support Terms"), minus AI Terms — that one is acknowledged
// separately/contextually on first AI feature use, not here (see
// AiTermsGate.js). Tab labels and checkbox wording are translated
// (legalGate.docs.*); the document text itself stays English — it's the
// binding version, and the Spanish screen says so.
const DOCUMENTS = [
  { tab: "terms", text: MASTER_TERMS },
  { tab: "aup", text: ACCEPTABLE_USE_POLICY },
  { tab: "privacy", text: PRIVACY_POLICY },
  { tab: "dpa", text: DATA_PROCESSING_TERMS },
];

function DocumentPanel({ text, scrolled, onScrolledToBottom }) {
  const { t } = useTranslation();
  const ref = useRef(null);

  const handleScroll = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_BOTTOM_THRESHOLD) {
      onScrolledToBottom();
    }
  }, [onScrolledToBottom]);

  // If the document is already fully visible with nothing to scroll (e.g.
  // a very large viewport), there's nothing to gate on — mark it read.
  const measureRef = useCallback(
    (node) => {
      ref.current = node;
      if (node && node.scrollHeight <= node.clientHeight) {
        onScrolledToBottom();
      }
    },
    [onScrolledToBottom],
  );

  return (
    <div
      ref={measureRef}
      onScroll={handleScroll}
      style={{
        height: "100%",
        overflowY: "auto",
        padding: "20px 24px",
        backgroundColor: "rgba(0,245,255,0.03)",
        border: `1px solid ${border}`,
        borderRadius: "8px",
        color: "rgba(255,255,255,0.75)",
        fontSize: "12px",
        lineHeight: "1.7",
        whiteSpace: "pre-wrap",
        fontFamily: "'Segoe UI', sans-serif",
      }}
    >
      {text}
      {!scrolled && (
        <div
          style={{
            position: "sticky",
            bottom: "-20px",
            marginTop: "20px",
            textAlign: "center",
            color: "rgba(255,170,0,0.8)",
            fontSize: "10px",
            letterSpacing: "1px",
            fontWeight: "700",
          }}
        >
          {t("legalGate.scrollToEnd")}
        </div>
      )}
    </div>
  );
}

// Non-dismissible full-screen clickwrap gate. Rendered by App.js in place
// of AppContent whenever the logged-in user's accepted versions don't
// match the current required versions (see legalStatus in the token/login
// response). There is deliberately no close/skip/backdrop-click affordance
// — accepting all 4 documents via POST /api/legal/accept is the only way
// out of this view.
export default function LegalAcceptanceGate({ token, onAccepted, onLogout }) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState(DOCUMENTS[0].tab);
  const [scrolled, setScrolled] = useState({});
  const [checked, setChecked] = useState({});
  const [authorityChecked, setAuthorityChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const allScrolled = DOCUMENTS.every((d) => scrolled[d.tab]);
  const allChecked = DOCUMENTS.every((d) => checked[d.tab]);
  const canAccept = allScrolled && allChecked && authorityChecked && !submitting;

  async function handleAccept() {
    if (!canAccept) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/legal/accept`,
        { authorityCheckbox: authorityChecked },
        { headers: { Authorization: "Bearer " + token } },
      );
      onAccepted(res.data);
    } catch (err) {
      setError(
        err.response?.data?.error || t("legalGate.acceptFailed"),
      );
    } finally {
      setSubmitting(false);
    }
  }

  const tabStyle = (tab) => ({
    flex: "1 0 auto",
    minHeight: "40px",
    whiteSpace: "nowrap",
    padding: "10px 8px",
    background: activeTab === tab ? "linear-gradient(135deg, #00f5ff, #0080ff)" : "none",
    color: activeTab === tab ? "#020817" : "rgba(0,245,255,0.6)",
    border: "none",
    borderRadius: "6px",
    cursor: "pointer",
    fontSize: "10px",
    fontWeight: "700",
    letterSpacing: "0.5px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "5px",
  });

  const activeDoc = DOCUMENTS.find((d) => d.tab === activeTab);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 20000,
        backgroundColor: "#020817",
        display: "flex",
        flexDirection: "column",
        fontFamily: "'Segoe UI', sans-serif",
      }}
    >
      {/* HEADER */}
      <div
        style={{
          padding: "20px 28px",
          borderBottom: `1px solid ${border}`,
          textAlign: "center",
          position: "relative",
        }}
      >
        {/* Not a "skip" affordance — this doesn't unblock the app, it just
            ends the session, same as choosing not to accept and walking
            away. Required so the gate doesn't fully trap a user who
            decides not to proceed. */}
        <div style={{ position: "absolute", top: "16px", left: "16px" }}>
          <LanguageToggle />
        </div>
        {onLogout && (
          <button
            onClick={onLogout}
            style={{
              position: "absolute",
              top: "16px",
              right: "16px",
              background: "none",
              border: `1px solid ${border}`,
              color: "rgba(255,68,102,0.7)",
              fontSize: "10px",
              padding: "4px 12px",
              minHeight: "32px",
              borderRadius: "4px",
              letterSpacing: "1px",
              cursor: "pointer",
            }}
          >
            {t("legalGate.logOut")}
          </button>
        )}
        <div
          style={{
            fontSize: "20px",
            fontWeight: "900",
            letterSpacing: "4px",
            background: "linear-gradient(135deg, #00f5ff, #0080ff)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            marginBottom: "8px",
            marginTop: "28px",
          }}
        >
          RIGIQ
        </div>
        <div style={{ color: "white", fontSize: "15px", fontWeight: "700" }}>
          {t("legalGate.beforeYouContinue")}
        </div>
        <p
          style={{
            color: "rgba(255,255,255,0.5)",
            fontSize: "12px",
            marginTop: "6px",
            maxWidth: "560px",
            marginLeft: "auto",
            marginRight: "auto",
            lineHeight: "1.6",
          }}
        >
          {t("legalGate.intro")}
        </p>
        {t("legalGate.englishOnly") && (
          <p data-testid="legal-english-only" style={{ color: "#ffcc66", fontSize: "12px", margin: "6px auto 0", maxWidth: "560px" }}>
            {t("legalGate.englishOnly")}
          </p>
        )}
      </div>

      {/* BODY */}
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          padding: "20px 28px",
          maxWidth: "760px",
          width: "100%",
          margin: "0 auto",
          boxSizing: "border-box",
        }}
      >
        <div
          role="tablist"
          aria-label={t("legalGate.documentsAria")}
          style={{
            display: "flex",
            gap: "6px",
            backgroundColor: "rgba(0,245,255,0.03)",
            border: `1px solid ${border}`,
            borderRadius: "8px",
            padding: "4px",
            marginBottom: "14px",
            overflowX: "auto",
          }}
        >
          {DOCUMENTS.map((d) => (
            <button
              key={d.tab}
              role="tab"
              aria-selected={activeTab === d.tab}
              onClick={() => setActiveTab(d.tab)}
              style={tabStyle(d.tab)}
            >
              {scrolled[d.tab] ? "✓" : "📄"} {t(`legalGate.docs.${d.tab}.tab`)}
            </button>
          ))}
        </div>

        <div style={{ flex: 1, minHeight: 0 }}>
          <DocumentPanel
            key={activeDoc.tab}
            text={activeDoc.text}
            scrolled={!!scrolled[activeDoc.tab]}
            onScrolledToBottom={() =>
              setScrolled((s) => (s[activeDoc.tab] ? s : { ...s, [activeDoc.tab]: true }))
            }
          />
        </div>

        {/* CHECKBOXES */}
        <div style={{ marginTop: "16px", display: "flex", flexDirection: "column", gap: "8px" }}>
          {DOCUMENTS.map((d) => (
            <label
              key={d.tab}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
                fontSize: "12px",
                color: scrolled[d.tab] ? "rgba(255,255,255,0.85)" : "rgba(255,255,255,0.3)",
                cursor: scrolled[d.tab] ? "pointer" : "not-allowed",
              }}
            >
              <input
                type="checkbox"
                checked={!!checked[d.tab]}
                disabled={!scrolled[d.tab]}
                onChange={(e) => setChecked((c) => ({ ...c, [d.tab]: e.target.checked }))}
              />
              <span>
                {t(`legalGate.docs.${d.tab}.agree`)}
                {!scrolled[d.tab] && (
                  <span style={{ color: "rgba(255,170,0,0.7)", fontSize: "10px", marginLeft: "6px" }}>
                    {t("legalGate.scrollFirst")}
                  </span>
                )}
              </span>
            </label>
          ))}
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              fontSize: "12px",
              color: allScrolled ? "rgba(255,255,255,0.85)" : "rgba(255,255,255,0.3)",
              cursor: allScrolled ? "pointer" : "not-allowed",
              marginTop: "4px",
              paddingTop: "8px",
              borderTop: `1px solid ${border}`,
            }}
          >
            <input
              type="checkbox"
              checked={authorityChecked}
              disabled={!allScrolled}
              onChange={(e) => setAuthorityChecked(e.target.checked)}
            />
            {t("legalGate.authority")}
          </label>
        </div>

        {error && (
          <div style={{ color: "#ff4466", fontSize: "12px", marginTop: "12px" }}>
            ⚠ {error}
          </div>
        )}

        <button
          onClick={handleAccept}
          disabled={!canAccept}
          style={{
            marginTop: "18px",
            width: "100%",
            background: canAccept
              ? "linear-gradient(135deg, #00f5ff, #0080ff)"
              : "rgba(0,245,255,0.1)",
            color: canAccept ? "#020817" : "rgba(0,245,255,0.3)",
            fontWeight: "700",
            fontSize: "13px",
            letterSpacing: "2px",
            padding: "14px",
            borderRadius: "8px",
            border: "none",
            cursor: canAccept ? "pointer" : "not-allowed",
            boxShadow: canAccept ? "0 0 20px rgba(0,245,255,0.3)" : "none",
          }}
        >
          {submitting ? t("legalGate.saving") : t("legalGate.accept")}
        </button>
      </div>
    </div>
  );
}